import { useState } from "react";

import { IconButton } from "ui/icon-button";
import { Panel } from "ui/panel";
import { Popover } from "ui/popover";

import { CampaignTemplateSummaryContent } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-content";
import { CampaignTemplateSummaryModal } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-modal";
import {
  type CampaignSummarySource,
  useCampaignTemplateSummary,
} from "containers/admin/clients/client/campaign-templates-page/use-campaign-template-summary";

import Icons from "assets";

type CampaignTemplateSummaryPanelProps = {
  source: CampaignSummarySource;
};

type SummaryViewState = "closed" | "popover" | "dialog";

export const CampaignTemplateSummaryPanel = ({ source }: CampaignTemplateSummaryPanelProps) => {
  const [viewState, setViewState] = useState<SummaryViewState>("closed");
  const { isLoading, generalFields, logicFields, creativeRows } =
    useCampaignTemplateSummary(source);

  const handleTogglePopover = () => {
    setViewState((prev) => (prev === "popover" ? "closed" : "popover"));
  };

  const handleClosePopover = () => {
    setViewState((prev) => (prev === "popover" ? "closed" : prev));
  };

  const handleExpand = () => {
    setViewState("dialog");
  };

  const handleCloseDialog = () => {
    setViewState("closed");
  };

  return (
    <>
      <Popover
        padding={0}
        placement="bottom-end"
        width={448}
        maxHeight="none"
        open={viewState === "popover"}
        onClose={handleClosePopover}
        triggerElement={
          <IconButton
            color="inherit"
            variant="outlined"
            size="large"
            aria-label="Generate summary"
            onClick={handleTogglePopover}>
            <Icons.Summary />
          </IconButton>
        }>
        <Panel
          title="Brief summary"
          action={
            <IconButton
              variant="text"
              color="inherit"
              aria-label="Expand to full screen"
              onClick={handleExpand}>
              <Icons.Expand width={16} height={16} />
            </IconButton>
          }>
          <CampaignTemplateSummaryContent
            isLoading={isLoading}
            generalFields={generalFields}
            logicFields={logicFields}
            creativeRows={creativeRows}
            fieldsLayout="column"
          />
        </Panel>
      </Popover>
      {viewState === "dialog" && (
        <CampaignTemplateSummaryModal source={source} onClose={handleCloseDialog} />
      )}
    </>
  );
};
